import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import { HubPage, HubPanel } from '../ui/HubShell';

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid rgba(15, 23, 42, 0.08)',
  borderRadius: 14,
  padding: 16,
  boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.04)',
};

const badge = (color: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  background: color,
  color: '#111827',
});

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,23,42,0.45)',
  zIndex: 50,
  display: 'flex',
  justifyContent: 'flex-end',
};

const drawer: React.CSSProperties = {
  width: 'min(480px, 100vw)',
  height: '100%',
  background: '#fff',
  boxShadow: '-8px 0 24px rgba(0,0,0,0.12)',
  padding: 20,
  overflowY: 'auto',
};

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm space-y-1">
      <span className="text-xs font-medium text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function inputCls(extra = '') {
  return `w-full border rounded-lg px-2.5 py-1.5 text-sm ${extra}`;
}

function EditDrawer({
  title,
  subtitle,
  onClose,
  onSave,
  saving,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  onSave: () => void;
  saving?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={overlay} onClick={onClose}>
      <div style={drawer} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{title}</h2>
            {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
          </div>
          <button type="button" className="text-slate-500 hover:text-slate-800" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="space-y-3">{children}</div>
        <div className="flex gap-2 mt-6 pt-4 border-t">
          <button
            type="button"
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm disabled:opacity-50"
            disabled={saving}
            onClick={onSave}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button type="button" className="px-4 py-2 border rounded-lg text-sm" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function PlatformAdminDashboard({ onOpenClient }: { onOpenClient: (id: string, name: string) => void }) {
  const [summary, setSummary] = useState<any>({});
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, c] = await Promise.all([
          apiService.platformDashboardSummary(),
          apiService.platformDashboardClientSummary(),
        ]);
        setSummary(s || {});
        setClients(Array.isArray(c) ? c : []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const kpis = [
    ['Total Clients', summary.total_clients],
    ['Active Clients', summary.active_clients],
    ['Trial', summary.trial_clients],
    ['Suspended', summary.suspended_clients],
    ['Total Devices', summary.total_devices],
    ['Online', summary.online_devices],
    ['Offline', summary.offline_devices],
    ['Windows', summary.windows_devices],
    ['Linux', summary.linux_devices],
    ['macOS', summary.macos_devices],
    ['Android', summary.android_devices],
    ['iOS', summary.ios_devices],
    ['Expiring Subs', summary.expiring_subscriptions],
    ['Over License', summary.over_license_clients],
    ['Pending Invoices', summary.pending_invoices],
    ['Monthly Revenue', summary.monthly_revenue],
  ];

  if (loading) return <div className="p-6 text-slate-500">Loading platform dashboard…</div>;

  return (
    <HubPage
      title="Platform Dashboard"
      subtitle="SaaS-wide view of clients, devices, subscriptions, and health."
      eyebrow="Command center"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        {kpis.map(([label, value]) => (
          <div key={String(label)} style={card}>
            <div className="text-xs text-slate-500 font-medium">{label}</div>
            <div className="text-xl font-bold text-slate-900 mt-1">{value ?? 0}</div>
          </div>
        ))}
      </div>
      <HubPanel>
        <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'var(--hub-font-display)' }}>Client summary</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="py-2">Client</th>
                <th>Code</th>
                <th>Status</th>
                <th>Plan</th>
                <th>Devices</th>
                <th>Win</th>
                <th>Lin</th>
                <th>Mac</th>
                <th>And</th>
                <th>iOS</th>
                <th>Users</th>
                <th>Sub</th>
                <th>Billing</th>
                <th>Patch %</th>
                <th>Alerts</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 font-medium">{c.name}</td>
                  <td>{c.code}</td>
                  <td><span style={badge('#dbeafe')}>{c.status}</span></td>
                  <td>{c.plan || '—'}</td>
                  <td>{c.device_count}</td>
                  <td>{c.os_breakdown?.windows ?? 0}</td>
                  <td>{c.os_breakdown?.linux ?? 0}</td>
                  <td>{c.os_breakdown?.macos ?? 0}</td>
                  <td>{c.os_breakdown?.android ?? 0}</td>
                  <td>{c.os_breakdown?.ios ?? 0}</td>
                  <td>{c.user_count}</td>
                  <td>{c.subscription_status || '—'}</td>
                  <td>{c.billing_status || '—'}</td>
                  <td>{c.patch_compliance_percent ?? '—'}</td>
                  <td>{c.critical_alerts ?? 0}</td>
                  <td>
                    <button
                      className="text-indigo-600 font-medium hover:underline"
                      onClick={() => onOpenClient(c.id, c.name)}
                    >
                      View / Configure
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </HubPanel>
    </HubPage>
  );
}

function toDateInput(iso?: string | null) {
  return iso ? String(iso).slice(0, 10) : '';
}

export function PlatformSubscriptionsPage() {
  const [rows, setRows] = useState<any[]>([]);
  const [filter, setFilter] = useState('');
  const [edit, setEdit] = useState<any | null>(null);
  const [draft, setDraft] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setRows(await apiService.platformListSubscriptions(filter || undefined));
  };
  useEffect(() => {
    load();
  }, [filter]);

  const openEdit = (r: any) => {
    setError(null);
    setEdit(r);
    setDraft({
      plan_name: r.plan_name || '',
      plan_code: r.plan_code || '',
      status: r.status || 'active',
      billing_cycle: r.billing_cycle || 'monthly',
      starts_at: toDateInput(r.starts_at),
      ends_at: toDateInput(r.ends_at),
      trial_ends_at: toDateInput(r.trial_ends_at),
      renewal_date: toDateInput(r.renewal_date),
      auto_renew: !!r.auto_renew,
      notes: r.notes || '',
    });
  };

  const save = async () => {
    if (!edit?.tenant_id) return;
    setSaving(true);
    setError(null);
    try {
      await apiService.platformUpdateSubscription(edit.tenant_id, {
        plan_name: draft.plan_name,
        plan_code: draft.plan_code,
        status: draft.status,
        billing_cycle: draft.billing_cycle,
        starts_at: draft.starts_at || null,
        ends_at: draft.ends_at || null,
        trial_ends_at: draft.trial_ends_at || null,
        renewal_date: draft.renewal_date || null,
        auto_renew: !!draft.auto_renew,
        notes: draft.notes || null,
      });
      setEdit(null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <HubPage title="Subscriptions" subtitle="View and edit each client’s plan, status, cycle, and dates." eyebrow="Commercial">
      <div className="flex gap-2 flex-wrap">
        {['', 'active', 'trial', 'expiring_soon', 'suspended', 'expired', 'cancelled'].map((f) => (
          <button
            key={f || 'all'}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${filter === f ? 'bg-indigo-600 text-white' : 'bg-white'}`}
          >
            {f || 'all'}
          </button>
        ))}
      </div>
      <div style={card} className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2">Client</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Cycle</th>
              <th>Start</th>
              <th>End</th>
              <th>Renewal</th>
              <th>Trial End</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="py-2">{r.client_name} ({r.client_code})</td>
                <td>{r.plan_name}</td>
                <td><span style={badge('#e0e7ff')}>{r.status}</span></td>
                <td>{r.billing_cycle}</td>
                <td>{r.starts_at?.slice(0, 10) || '—'}</td>
                <td>{r.ends_at?.slice(0, 10) || '—'}</td>
                <td>{r.renewal_date?.slice(0, 10) || '—'}</td>
                <td>{r.trial_ends_at?.slice(0, 10) || '—'}</td>
                <td className="space-x-2 whitespace-nowrap">
                  <button className="text-indigo-600 font-medium" onClick={() => openEdit(r)}>Edit</button>
                  <button className="text-indigo-600" onClick={async () => { await apiService.platformRenewSubscription(r.tenant_id); load(); }}>Renew</button>
                  <button className="text-indigo-600" onClick={async () => { await apiService.platformExtendTrial(r.tenant_id, 14); load(); }}>Extend trial</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="py-6 text-center text-slate-500">No subscriptions.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {edit && (
        <EditDrawer
          title="Edit subscription"
          subtitle={`${edit.client_name} (${edit.client_code})`}
          onClose={() => setEdit(null)}
          onSave={save}
          saving={saving}
        >
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
          <Field label="Plan name">
            <input className={inputCls()} value={draft.plan_name} onChange={(e) => setDraft({ ...draft, plan_name: e.target.value })} />
          </Field>
          <Field label="Plan code">
            <input className={inputCls()} value={draft.plan_code} onChange={(e) => setDraft({ ...draft, plan_code: e.target.value })} />
          </Field>
          <Field label="Status">
            <select className={inputCls()} value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
              {['active', 'trial', 'suspended', 'expired', 'cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Billing cycle">
            <select className={inputCls()} value={draft.billing_cycle} onChange={(e) => setDraft({ ...draft, billing_cycle: e.target.value })}>
              <option value="monthly">monthly</option>
              <option value="annual">annual</option>
              <option value="yearly">yearly</option>
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts"><input type="date" className={inputCls()} value={draft.starts_at} onChange={(e) => setDraft({ ...draft, starts_at: e.target.value })} /></Field>
            <Field label="Ends"><input type="date" className={inputCls()} value={draft.ends_at} onChange={(e) => setDraft({ ...draft, ends_at: e.target.value })} /></Field>
            <Field label="Trial ends"><input type="date" className={inputCls()} value={draft.trial_ends_at} onChange={(e) => setDraft({ ...draft, trial_ends_at: e.target.value })} /></Field>
            <Field label="Renewal"><input type="date" className={inputCls()} value={draft.renewal_date} onChange={(e) => setDraft({ ...draft, renewal_date: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={!!draft.auto_renew} onChange={(e) => setDraft({ ...draft, auto_renew: e.target.checked })} />
            Auto renew
          </label>
          <Field label="Notes">
            <textarea className={inputCls()} rows={3} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </Field>
        </EditDrawer>
      )}
    </HubPage>
  );
}

export function PlatformBillingPage() {
  const [rows, setRows] = useState<any[]>([]);
  const [edit, setEdit] = useState<any | null>(null);
  const [draft, setDraft] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => apiService.platformListBilling().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const openEdit = (r: any) => {
    setError(null);
    setEdit(r);
    setDraft({
      billing_name: r.billing_name || '',
      billing_contact_name: r.billing_contact_name || '',
      billing_email: r.billing_email || '',
      billing_phone: r.billing_phone || '',
      tax_number: r.tax_number || '',
      currency: r.currency || 'INR',
      payment_terms: r.payment_terms || '',
      invoice_prefix: r.invoice_prefix || '',
      plan_amount: r.plan_amount ?? 0,
      add_on_charges: r.add_on_charges ?? 0,
      discount: r.discount ?? 0,
      tax_percent: r.tax_percent ?? 0,
      payment_method: r.payment_method || 'manual',
      payment_status: r.payment_status || 'pending',
      due_amount: r.due_amount ?? 0,
      due_date: toDateInput(r.due_date),
      notes: r.notes || '',
    });
  };

  const save = async () => {
    if (!edit?.tenant_id) return;
    setSaving(true);
    setError(null);
    try {
      await apiService.platformUpdateBilling(edit.tenant_id, {
        ...draft,
        plan_amount: Number(draft.plan_amount) || 0,
        add_on_charges: Number(draft.add_on_charges) || 0,
        discount: Number(draft.discount) || 0,
        tax_percent: Number(draft.tax_percent) || 0,
        due_amount: Number(draft.due_amount) || 0,
        due_date: draft.due_date || null,
      });
      setEdit(null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <HubPage title="Billing" eyebrow="Commercial" subtitle="Manual billing profiles — edit contact, amounts, and payment status (no payment gateway).">
      <div style={card} className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2">Client</th>
              <th>Contact</th>
              <th>Email</th>
              <th>Tax #</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Due</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="py-2">{r.client_name}</td>
                <td>{r.billing_contact_name || '—'}</td>
                <td>{r.billing_email || '—'}</td>
                <td>{r.tax_number || '—'}</td>
                <td>{r.currency} {r.plan_amount ?? 0}</td>
                <td>{r.payment_status}</td>
                <td>{r.due_amount ?? 0}</td>
                <td>
                  <button className="text-indigo-600 font-medium" onClick={() => openEdit(r)}>Edit</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="py-6 text-center text-slate-500">No billing profiles.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {edit && (
        <EditDrawer
          title="Edit billing"
          subtitle={`${edit.client_name} (${edit.client_code})`}
          onClose={() => setEdit(null)}
          onSave={save}
          saving={saving}
        >
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
          <Field label="Billing name"><input className={inputCls()} value={draft.billing_name} onChange={(e) => setDraft({ ...draft, billing_name: e.target.value })} /></Field>
          <Field label="Contact name"><input className={inputCls()} value={draft.billing_contact_name} onChange={(e) => setDraft({ ...draft, billing_contact_name: e.target.value })} /></Field>
          <Field label="Billing email"><input className={inputCls()} value={draft.billing_email} onChange={(e) => setDraft({ ...draft, billing_email: e.target.value })} /></Field>
          <Field label="Phone"><input className={inputCls()} value={draft.billing_phone} onChange={(e) => setDraft({ ...draft, billing_phone: e.target.value })} /></Field>
          <Field label="Tax number"><input className={inputCls()} value={draft.tax_number} onChange={(e) => setDraft({ ...draft, tax_number: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Currency"><input className={inputCls()} value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })} /></Field>
            <Field label="Payment status">
              <select className={inputCls()} value={draft.payment_status} onChange={(e) => setDraft({ ...draft, payment_status: e.target.value })}>
                {['pending', 'paid', 'overdue', 'waived', 'cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Plan amount"><input type="number" className={inputCls()} value={draft.plan_amount} onChange={(e) => setDraft({ ...draft, plan_amount: e.target.value })} /></Field>
            <Field label="Due amount"><input type="number" className={inputCls()} value={draft.due_amount} onChange={(e) => setDraft({ ...draft, due_amount: e.target.value })} /></Field>
            <Field label="Add-on charges"><input type="number" className={inputCls()} value={draft.add_on_charges} onChange={(e) => setDraft({ ...draft, add_on_charges: e.target.value })} /></Field>
            <Field label="Discount"><input type="number" className={inputCls()} value={draft.discount} onChange={(e) => setDraft({ ...draft, discount: e.target.value })} /></Field>
            <Field label="Tax %"><input type="number" className={inputCls()} value={draft.tax_percent} onChange={(e) => setDraft({ ...draft, tax_percent: e.target.value })} /></Field>
            <Field label="Due date"><input type="date" className={inputCls()} value={draft.due_date} onChange={(e) => setDraft({ ...draft, due_date: e.target.value })} /></Field>
          </div>
          <Field label="Payment method"><input className={inputCls()} value={draft.payment_method} onChange={(e) => setDraft({ ...draft, payment_method: e.target.value })} /></Field>
          <Field label="Payment terms"><input className={inputCls()} value={draft.payment_terms} onChange={(e) => setDraft({ ...draft, payment_terms: e.target.value })} /></Field>
          <Field label="Invoice prefix"><input className={inputCls()} value={draft.invoice_prefix} onChange={(e) => setDraft({ ...draft, invoice_prefix: e.target.value })} /></Field>
          <Field label="Notes"><textarea className={inputCls()} rows={3} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></Field>
        </EditDrawer>
      )}
    </HubPage>
  );
}

export function PlatformLicenseUsagePage() {
  const [rows, setRows] = useState<any[]>([]);
  const [edit, setEdit] = useState<any | null>(null);
  const [draft, setDraft] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => apiService.platformListLicenseUsage().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const openEdit = (r: any) => {
    setError(null);
    setEdit(r);
    const lim = r.limits || {};
    setDraft({
      max_users: lim.max_users ?? r.users_limit ?? 50,
      max_devices: lim.max_devices ?? r.devices_limit ?? 100,
      max_windows_devices: lim.max_windows_devices ?? '',
      max_linux_devices: lim.max_linux_devices ?? '',
      max_macos_devices: lim.max_macos_devices ?? '',
      max_android_devices: lim.max_android_devices ?? '',
      max_ios_devices: lim.max_ios_devices ?? '',
      max_storage_gb: lim.max_storage_gb ?? r.storage_limit_gb ?? 5,
      max_ai_minutes: lim.max_ai_minutes ?? '',
      max_remote_execution_per_month: lim.max_remote_execution_per_month ?? '',
    });
  };

  const numOrNull = (v: any) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const save = async () => {
    if (!edit?.tenant_id) return;
    setSaving(true);
    setError(null);
    try {
      await apiService.platformUpdateLicenseLimits(edit.tenant_id, {
        max_users: numOrNull(draft.max_users),
        max_devices: numOrNull(draft.max_devices),
        max_windows_devices: numOrNull(draft.max_windows_devices),
        max_linux_devices: numOrNull(draft.max_linux_devices),
        max_macos_devices: numOrNull(draft.max_macos_devices),
        max_android_devices: numOrNull(draft.max_android_devices),
        max_ios_devices: numOrNull(draft.max_ios_devices),
        max_storage_gb: numOrNull(draft.max_storage_gb),
        max_ai_minutes: numOrNull(draft.max_ai_minutes),
        max_remote_execution_per_month: numOrNull(draft.max_remote_execution_per_month),
      });
      setEdit(null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <HubPage title="License & Usage" eyebrow="Commercial" subtitle="Live usage vs limits. Edit caps per client.">
      <div style={card} className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2">Client</th>
              <th>Users</th>
              <th>Devices</th>
              <th>Win</th>
              <th>Lin</th>
              <th>Mac</th>
              <th>And</th>
              <th>iOS</th>
              <th>Storage</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.tenant_id} className="border-b border-slate-100">
                <td className="py-2">{r.client_name}</td>
                <td>{r.users_used}/{r.users_limit ?? '∞'}</td>
                <td>{r.devices_used}/{r.devices_limit ?? '∞'}</td>
                <td>{r.windows}</td>
                <td>{r.linux}</td>
                <td>{r.macos}</td>
                <td>{r.android}</td>
                <td>{r.ios}</td>
                <td>{r.storage_used_gb}/{r.storage_limit_gb}</td>
                <td><span style={badge(r.status === 'over_limit' ? '#fecaca' : r.status === 'critical' ? '#fde68a' : '#d1fae5')}>{r.status}</span></td>
                <td>
                  <button className="text-indigo-600 font-medium" onClick={() => openEdit(r)}>Edit limits</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <EditDrawer
          title="Edit license limits"
          subtitle={`${edit.client_name} (${edit.client_code}) — used: ${edit.users_used} users / ${edit.devices_used} devices`}
          onClose={() => setEdit(null)}
          onSave={save}
          saving={saving}
        >
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Max users"><input type="number" className={inputCls()} value={draft.max_users} onChange={(e) => setDraft({ ...draft, max_users: e.target.value })} /></Field>
            <Field label="Max devices"><input type="number" className={inputCls()} value={draft.max_devices} onChange={(e) => setDraft({ ...draft, max_devices: e.target.value })} /></Field>
            <Field label="Max Windows"><input type="number" className={inputCls()} value={draft.max_windows_devices} onChange={(e) => setDraft({ ...draft, max_windows_devices: e.target.value })} /></Field>
            <Field label="Max Linux"><input type="number" className={inputCls()} value={draft.max_linux_devices} onChange={(e) => setDraft({ ...draft, max_linux_devices: e.target.value })} /></Field>
            <Field label="Max macOS"><input type="number" className={inputCls()} value={draft.max_macos_devices} onChange={(e) => setDraft({ ...draft, max_macos_devices: e.target.value })} /></Field>
            <Field label="Max Android"><input type="number" className={inputCls()} value={draft.max_android_devices} onChange={(e) => setDraft({ ...draft, max_android_devices: e.target.value })} /></Field>
            <Field label="Max iOS"><input type="number" className={inputCls()} value={draft.max_ios_devices} onChange={(e) => setDraft({ ...draft, max_ios_devices: e.target.value })} /></Field>
            <Field label="Max storage (GB)"><input type="number" className={inputCls()} value={draft.max_storage_gb} onChange={(e) => setDraft({ ...draft, max_storage_gb: e.target.value })} /></Field>
            <Field label="Max AI minutes"><input type="number" className={inputCls()} value={draft.max_ai_minutes} onChange={(e) => setDraft({ ...draft, max_ai_minutes: e.target.value })} /></Field>
            <Field label="Max remote exec / month"><input type="number" className={inputCls()} value={draft.max_remote_execution_per_month} onChange={(e) => setDraft({ ...draft, max_remote_execution_per_month: e.target.value })} /></Field>
          </div>
        </EditDrawer>
      )}
    </HubPage>
  );
}

const OS_KEYS = ['windows', 'linux', 'macos', 'android', 'ios'] as const;
const MODULE_KEYS = ['patch-management', 'remote-execution', 'compliance', 'analytics', 'license-management'] as const;
const DEFAULT_OS_MODES: Record<string, string[]> = {
  windows: ['AGENT_MANAGED'],
  linux: ['AGENT_MANAGED'],
  macos: ['AGENT_MANAGED'],
  android: ['WORK_PROFILE', 'DEVICE_OWNER'],
  ios: ['USER_ENROLLMENT'],
};

export function PlatformEntitlementsPage() {
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () => apiService.platformEntitlementsMatrix().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const toggleOs = async (row: any, os: string, enabled: boolean) => {
    const key = `${row.tenant_id}:os:${os}`;
    setBusy(key);
    setMsg(null);
    try {
      await apiService.platformPutOsEntitlements(row.tenant_id, [
        { platform: os, enabled, allowed_modes: enabled ? (DEFAULT_OS_MODES[os] || []) : [] },
      ]);
      setMsg(`Updated ${row.client_name} ${os}`);
      await load();
    } catch (e: any) {
      setMsg(e?.message || 'OS update failed');
    } finally {
      setBusy(null);
    }
  };

  const toggleModule = async (row: any, mod: string, enabled: boolean) => {
    const key = `${row.tenant_id}:mod:${mod}`;
    setBusy(key);
    setMsg(null);
    try {
      await apiService.platformUpdateModules(row.tenant_id, { [mod]: enabled });
      setMsg(`Updated ${row.client_name} module ${mod}`);
      await load();
    } catch (e: any) {
      setMsg(e?.message || 'Module update failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <HubPage title="Module & OS Entitlements" eyebrow="Access" subtitle="Toggle OS platforms and key modules per client. Changes save immediately.">
      {msg && <div className="text-sm text-slate-700 bg-slate-50 border rounded-lg px-3 py-2">{msg}</div>}
      <div style={card} className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2">Client</th>
              {OS_KEYS.map((os) => <th key={os}>{os.slice(0, 3)}</th>)}
              {MODULE_KEYS.map((m) => <th key={m}>{m.split('-')[0]}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.tenant_id} className="border-b border-slate-100">
                <td className="py-2 font-medium">{r.client_name}</td>
                {OS_KEYS.map((os) => {
                  const on = !!r[os];
                  const key = `${r.tenant_id}:os:${os}`;
                  return (
                    <td key={os}>
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={busy === key}
                        onChange={(e) => toggleOs(r, os, e.target.checked)}
                        title={`${os} entitlement`}
                      />
                    </td>
                  );
                })}
                {MODULE_KEYS.map((m) => {
                  const on = !!r.modules?.[m];
                  const key = `${r.tenant_id}:mod:${m}`;
                  return (
                    <td key={m}>
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={busy === key}
                        onChange={(e) => toggleModule(r, m, e.target.checked)}
                        title={m}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">
        For full page/action entitlement editors, open Clients → Configure → OS / Page / Action tabs.
      </p>
    </HubPage>
  );
}

export function PlatformUsersPage() {
  const empty = { username: '', password: '', email: '', full_name: '', role: 'PLATFORM_VIEWER' };
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState({ ...empty });
  const [editingId, setEditingId] = useState<string | null>(null);
  const load = () => apiService.platformListUsers().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ ...empty });
    setEditingId(null);
  };

  const startEdit = (u: any) => {
    setEditingId(u.id);
    setForm({
      username: u.username || '',
      password: '',
      email: u.email || '',
      full_name: u.full_name || '',
      role: u.role || 'PLATFORM_VIEWER',
    });
  };

  const save = async () => {
    if (editingId) {
      const body: Record<string, unknown> = {
        full_name: form.full_name || null,
        email: form.email || null,
        role: form.role,
      };
      if (form.password) body.password = form.password;
      await apiService.platformUpdateUser(editingId, body);
    } else {
      await apiService.platformCreateUser(form);
    }
    resetForm();
    load();
  };

  return (
    <HubPage title="Platform Users" subtitle="Create and manage platform staff accounts." eyebrow="Access">
      <div style={card} className="grid md:grid-cols-5 gap-2 items-end">
        {(['username', 'password', 'email', 'full_name'] as const).map((k) => (
          <div key={k}>
            <label className="text-xs text-slate-500">{k}{k === 'password' && editingId ? ' (optional)' : ''}</label>
            <input
              className="w-full border rounded px-2 py-1.5"
              type={k === 'password' ? 'password' : 'text'}
              value={(form as any)[k]}
              disabled={k === 'username' && !!editingId}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            />
          </div>
        ))}
        <div>
          <label className="text-xs text-slate-500">role</label>
          <select className="w-full border rounded px-2 py-1.5" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {['PLATFORM_ADMIN', 'PLATFORM_SUPPORT', 'PLATFORM_BILLING', 'PLATFORM_VIEWER'].map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button className="px-3 py-2 bg-indigo-600 text-white rounded-lg" onClick={save}>
            {editingId ? 'Save' : 'Create'}
          </button>
          {editingId && (
            <button className="px-3 py-2 border rounded-lg" onClick={resetForm}>Cancel</button>
          )}
        </div>
      </div>
      <div style={card}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2">Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-b border-slate-100">
                <td className="py-2">{u.full_name || u.username}</td>
                <td>{u.email || '—'}</td>
                <td>{u.role}</td>
                <td>{u.is_active === false ? 'Disabled' : 'Active'}</td>
                <td className="space-x-3">
                  <button className="text-indigo-600" onClick={() => startEdit(u)}>Edit</button>
                  {u.is_active === false ? (
                    <button
                      className="text-emerald-600"
                      onClick={async () => {
                        await apiService.platformUpdateUser(u.id, { is_active: true });
                        load();
                      }}
                    >
                      Enable
                    </button>
                  ) : (
                    <button
                      className="text-red-600"
                      onClick={async () => {
                        if (!window.confirm(`Disable ${u.username || u.email}?`)) return;
                        await apiService.platformDisableUser(u.id);
                        load();
                      }}
                    >
                      Disable
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </HubPage>
  );
}

export function PlatformAuditLogsPage() {
  const [rows, setRows] = useState<any[]>([]);
  const [tenantFilter, setTenantFilter] = useState('');
  const [tenants, setTenants] = useState<any[]>([]);

  useEffect(() => {
    apiService.platformListTenants().then(setTenants).catch(() => setTenants([]));
  }, []);

  useEffect(() => {
    apiService.platformAudit(200, tenantFilter || undefined).then(setRows).catch(() => setRows([]));
  }, [tenantFilter]);

  return (
    <HubPage
      title="Platform Audit Logs"
      subtitle="Read-only activity trail (filter by tenant)."
      eyebrow="Ops"
      action={
        <select
          className="border rounded-lg px-3 py-1.5 text-sm"
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
        >
          <option value="">All tenants</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.id}>{t.name} ({t.code})</option>
          ))}
        </select>
      }
    >
      <div style={card} className="overflow-x-auto max-h-[70vh]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2">When</th>
              <th>Actor</th>
              <th>Action</th>
              <th>Entity</th>
              <th>Tenant</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="py-2 whitespace-nowrap">{r.created_at?.replace('T', ' ').slice(0, 19)}</td>
                <td>{r.actor || r.actor_username || '—'}</td>
                <td>{r.action}</td>
                <td>{r.entity_type} {r.entity_id || ''}</td>
                <td>{r.tenant_id || '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-slate-500">No audit entries.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </HubPage>
  );
}

export function PlatformSystemHealthPage() {
  const [data, setData] = useState<any>({ services: [] });
  const load = () => apiService.platformSystemHealth().then(setData).catch(() => setData({ services: [] }));
  useEffect(() => { load(); }, []);
  return (
    <HubPage
      title="System Health"
      subtitle="Read-only probes (API, DB, optional MDM). No restart actions."
      eyebrow="Ops"
      action={
        <button type="button" className="px-3 py-1.5 border rounded-lg" onClick={load}>Refresh</button>
      }
    >
      <div style={card}>Overall: <strong>{data.overall}</strong></div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {(data.services || []).map((s: any) => (
          <div key={s.service} style={card}>
            <div className="font-semibold">{s.service}</div>
            <div className="text-sm mt-1">Status: {s.status}</div>
            <div className="text-xs text-slate-500">RTT: {s.response_time_ms ?? '—'} ms</div>
            {s.error && <div className="text-xs text-red-600 mt-1">{s.error}</div>}
          </div>
        ))}
      </div>
    </HubPage>
  );
}

/** Friendly labels and guidance for the settings we know about.
 *  Anything not listed still renders, keyed by its raw name. */
const SETTING_HELP: Record<string, { label: string; hint?: string; warn?: string }> = {
  platform_name: { label: 'Platform name' },
  default_support_email: { label: 'Support email' },
  default_trial_days: { label: 'Trial length (days)' },
  default_currency: { label: 'Currency' },
  default_enrollment_expiry_hours: { label: 'Enrollment invite expiry (hours)' },
  require_enrollment_invite: { label: 'Require an enrollment invite', hint: 'true or false.' },
  default_heartbeat_interval_seconds: { label: 'Agent heartbeat interval (seconds)' },
  device_token_lifetime_hours: {
    label: 'Device token lifetime (hours)',
    hint:
      'How long a newly issued agent token stays valid. Between 1 and 8760 (a year); ' +
      'values outside that are clamped. Shorter is safer, but an agent that cannot ' +
      'renew itself is locked out when its token lapses — keep this comfortably longer ' +
      'than the longest a machine is normally switched off.',
  },
  device_token_renewal_grace_hours: {
    label: 'Device token renewal grace (hours)',
    hint:
      'How long after expiry an agent may still exchange a lapsed token for a fresh ' +
      'one. The token signature must still verify and must name that device, so this ' +
      'does not accept forged tokens. Set to 0 to disable lapsed renewal entirely, ' +
      'which means a machine offline past its expiry has to be re-enrolled by hand.',
  },
  require_device_token: {
    label: 'Require device token',
    warn:
      'Wired and currently OFF by default. Turn this on only after every device ' +
      'you intend to keep is running TokenRenewer and presenting a valid device ' +
      'token. Offline or tokenless agents will be rejected (401) and must re-enroll. ' +
      'UEM_REQUIRE_DEVICE_TOKEN=true in the environment still forces enforcement on.',
  },
};

export function PlatformSettingsPage() {
  const [settings, setSettings] = useState<Record<string, any>>({});
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    apiService.platformGetSettings().then((d) => setSettings(d.settings || {})).catch(() => setSettings({}));
  }, []);

  const keys = Object.keys(settings).length
    ? Object.keys(settings)
    : [
        'platform_name',
        'default_support_email',
        'default_trial_days',
        'default_currency',
        'default_enrollment_expiry_hours',
        'require_enrollment_invite',
        'require_device_token',
        'default_heartbeat_interval_seconds',
        'device_token_lifetime_hours',
        'device_token_renewal_grace_hours',
      ];

  return (
    <HubPage title="Platform Settings" eyebrow="Ops" subtitle="Editable platform defaults (secrets are masked and not overwritten)." className="max-w-2xl">
      <div style={card} className="space-y-3">
        {keys.map((k) => {
          const help = SETTING_HELP[k];
          return (
            <div key={k}>
              <label className="text-xs text-slate-500">{help?.label ?? k}</label>
              <input
                className="w-full border rounded px-2 py-1.5"
                value={String(settings[k] ?? '')}
                onChange={(e) => setSettings({ ...settings, [k]: e.target.value })}
              />
              {help?.hint && <div className="text-xs text-slate-500 mt-1">{help.hint}</div>}
              {help?.warn && <div className="text-xs text-amber-700 mt-1">{help.warn}</div>}
            </div>
          );
        })}
        <button
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            setMsg('');
            try {
              const parsed: Record<string, unknown> = {};
              for (const [k, v] of Object.entries(settings)) {
                if (v === 'true' || v === 'false') parsed[k] = v === 'true';
                else if (!Number.isNaN(Number(v)) && String(v).trim() !== '' && /^-?\d+(\.\d+)?$/.test(String(v))) parsed[k] = Number(v);
                else parsed[k] = v;
              }
              await apiService.platformUpdateSettings(parsed);
              setMsg('Saved');
            } catch (e: any) {
              setMsg(e?.message || 'Save failed');
            } finally {
              setSaving(false);
            }
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {msg && <div className="text-green-700 text-sm">{msg}</div>}
      </div>
    </HubPage>
  );
}

/** Platform Super Admin: entitle Software Metering per tenant + optional collection override. */
export function PlatformSoftwareMeteringPage() {
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () =>
    apiService.platformSoftwareMeteringCoverage().then(setRows).catch(() => setRows([]));
  useEffect(() => {
    load();
  }, []);

  return (
    <HubPage
      title="Software Metering"
      eyebrow="Coverage"
      subtitle="Entitle the module per client and optionally override collection. Tenant admins still control detailed policy. View reports via Client Portal."
    >
      {msg && <div className="text-sm text-slate-700 bg-slate-50 border rounded-lg px-3 py-2">{msg}</div>}
      <div style={card} className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2">Client</th>
              <th>Page entitled</th>
              <th>Collection</th>
              <th>Devices</th>
              <th>Reporting (7d)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const entitleKey = `${r.tenant_id}:entitle`;
              const collKey = `${r.tenant_id}:collection`;
              return (
                <tr key={r.tenant_id} className="border-b border-slate-100">
                  <td className="py-2 font-medium">
                    {r.client_name}
                    {r.client_code ? (
                      <span className="text-slate-400 ml-1 text-xs">({r.client_code})</span>
                    ) : null}
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!r.page_entitled}
                      disabled={busy === entitleKey}
                      onChange={async (e) => {
                        setBusy(entitleKey);
                        setMsg(null);
                        try {
                          await apiService.platformEntitleSoftwareMetering(r.tenant_id, e.target.checked);
                          setMsg(`Entitlement updated for ${r.client_name}`);
                          await load();
                        } catch (err: any) {
                          setMsg(err?.message || 'Entitle failed');
                        } finally {
                          setBusy(null);
                        }
                      }}
                      title="page.software_metering.view"
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={!!r.collection_enabled}
                      disabled={busy === collKey || !r.page_entitled}
                      onChange={async (e) => {
                        setBusy(collKey);
                        setMsg(null);
                        try {
                          await apiService.platformSetSoftwareMeteringCollection(
                            r.tenant_id,
                            e.target.checked
                          );
                          setMsg(`Collection ${e.target.checked ? 'enabled' : 'disabled'} for ${r.client_name}`);
                          await load();
                        } catch (err: any) {
                          setMsg(err?.message || 'Collection update failed');
                        } finally {
                          setBusy(null);
                        }
                      }}
                      title="Override tenant collection policy"
                    />
                  </td>
                  <td>{r.device_count ?? 0}</td>
                  <td>{r.reporting_devices_7d ?? 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!rows.length && <p className="text-slate-400 text-sm mt-2">No tenants</p>}
      </div>
    </HubPage>
  );
}
